import { useEffect } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { useStores } from '../../../../stores/useStores';
import { CoinWithValue } from '../../../CoinWithValue/CoinWithValue';
import Crown from '../../../SVGIcons/Crown';

import styles from './JackpotNavWidget.module.scss';

/**
 * Round nav-rail icon for the Crown Jackpot (FE_DESIGN_SPEC §7.4 variant C): circular crown badge,
 * `CROWN JP` label, coin + the top level pot beneath — the biggest prize on the ladder, by
 * association priority.
 *
 * Display and navigation only — it opens game-ui's jackpot popup and never writes participation.
 * No lobby widget does: the popup is the single opt-in surface. Renders nothing unless the jackpot
 * is visible to this player, and reads the one shared JackpotStore poll.
 */
export const JackpotNavWidget = observer(({ dataTestId = 'jackpot-nav-widget' }: { dataTestId?: string }) => {
  const { t } = useTranslation();
  const { coinTypeStore, gameUiStore, jackpotStore } = useStores();

  const { canShowWidget, topLevelAmountLabelCompact } = jackpotStore;

  // Deduped in the store: several lobby surfaces are one exposure. See `reportWidgetShown`.
  useEffect(() => {
    if (canShowWidget) {
      jackpotStore.reportWidgetShown();
    }
  }, [canShowWidget, jackpotStore]);

  if (!canShowWidget) {
    return null;
  }

  return (
    <div className="home-widget" data-testid={dataTestId}>
      <div className={styles.widget}>
        <div
          className={classNames(styles.badge, 'circle-parent')}
          data-testid="jackpot-nav-widget-button"
          onClick={() => gameUiStore.openJackpotDialog()}
        >
          <div className="circle" />
          {/*
            TODO(KRA-303 assets): placeholder badge — the Figma circular crown artwork is not in the
            repo. The existing Crown glyph sits inside the rail's standard `.circle`, so the swap to
            the real asset is an <img> in place of this span and nothing else moves.
          */}
          <span aria-hidden="true" className={styles.crown}>
            <Crown />
          </span>
        </div>
        <div className={styles.label}>{t('jackpot.navLabel')}</div>
        <CoinWithValue
          className={styles.amount}
          coinSide="left"
          coinType={coinTypeStore.coinType}
          dataTestId="jackpot-nav-widget-amount"
          valueClassName={styles.amountValue}
        >
          {topLevelAmountLabelCompact}
        </CoinWithValue>
      </div>
    </div>
  );
});
